import useAIFeedbackStore, {
	type AIFeedbackState,
} from "@features/session/hooks/useAIFeedbackStore";
import useRealtimeAudioEmotion from "@features/session/hooks/useRealtimeAudioEmotion";
import { useCallback, useEffect, useRef } from "react";
import { Caption } from "react-native-paper";
import EmotionScoreDisplay from "./EmotionScoreDisplay";

interface HumeAudioAnalyzerProps {
	itemId: string;
	audioData?: string | undefined;
	isCleared?: boolean;
	isAnswered?: boolean;
	chunkIndex: number;
}

type EmotionPrediction = {
	name: string;
	score: number;
};

const HumeAudioAnalyzer = ({
	itemId,
	audioData,
	isCleared,
	isAnswered,
	chunkIndex,
}: HumeAudioAnalyzerProps) => {
	const updateEmotionPredictions = useAIFeedbackStore(
		(state: AIFeedbackState) => state.updateEmotionPredictions,
	);

	const chunkCountRef = useRef(0);
	const currentChunkIndexRef = useRef(0);

	const { predictions, sendAudio } = useRealtimeAudioEmotion({
		apiKey: process.env.EXPO_PUBLIC_HUME_API_KEY!,
		modelName: "prosody",
		isCleared,
	});

	const getTimeRange = useCallback((): string => {
		const startTime = chunkCountRef.current * 2;
		const endTime = startTime + 2;
		return `${startTime}-${endTime}sec`;
	}, []);

	useEffect(() => {
		if (audioData) {
			try {
				if (currentChunkIndexRef.current !== chunkIndex) {
					sendAudio(audioData);
					chunkCountRef.current += 1;
				}
			} catch (error) {
				console.error("Error processing audio chunk:", error);
			}
		}
	}, [audioData, sendAudio, chunkIndex]);

	useEffect(() => {
		if (predictions && Array.isArray(predictions) && predictions.length > 0) {
			currentChunkIndexRef.current = chunkIndex;
			const timeRange = getTimeRange();
			const latestPrediction = predictions[predictions.length - 1];
			const topEmotions = latestPrediction.emotions
				.sort((a: EmotionPrediction, b: EmotionPrediction) => b.score - a.score)
				.slice(0, 7)
				.map(({ name, score }: EmotionPrediction) => ({ name, score }));
			updateEmotionPredictions(itemId, timeRange, topEmotions);
		}
	}, [predictions, itemId, updateEmotionPredictions, getTimeRange, chunkIndex]);

	useEffect(() => {
		if (isCleared) {
			chunkCountRef.current = 0;
		}
	}, [isCleared]);

	return (
		<>
			{!isAnswered &&
				predictions &&
				Array.isArray(predictions) &&
				predictions.length > 0 && (
					<>
						<Caption
							style={{
								paddingBottom: 16,
								paddingHorizontal: 16,
								textAlign: "center",
							}}
						>
							{"Real time emotion analysis"}
						</Caption>
						<EmotionScoreDisplay predictions={predictions} />
					</>
				)}
			{typeof predictions === "string" && (
				<Caption
					style={{
						paddingBottom: 16,
						paddingHorizontal: 16,
						textAlign: "center",
					}}
				>
					{predictions}
				</Caption>
			)}
		</>
	);
};

export default HumeAudioAnalyzer;
